import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../api";
import { asSentence } from "../lib/tasks";

function useRefresh() {
  const queryClient = useQueryClient();
  return (taskId?: string) => {
    queryClient.invalidateQueries({ queryKey: ["state"] });
    queryClient.invalidateQueries({ queryKey: ["tasks"] });
    queryClient.invalidateQueries({ queryKey: ["run"] });
    if (taskId) queryClient.invalidateQueries({ queryKey: ["task", taskId] });
  };
}

const fail = (title: string) => (error: Error) => toast.error(title, { description: error.message });

export function useMarkMergeReady() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ itemId }: { itemId: string; taskId: string }) => api.markMergeReady(itemId),
    onSuccess: ({ item }, { taskId }) => {
      toast.success(`PR #${item.prNumber} is ready for review`);
      refresh(taskId);
    },
    onError: fail("Could not mark the pull request ready"),
  });
}

export function useApproveAndMerge() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ itemId }: { itemId: string; taskId: string }) => api.approveAndMerge(itemId),
    onSuccess: ({ item }, { taskId }) => {
      toast.success(
        item.status === "merged" ? `Squash-merged PR #${item.prNumber}` : `Approved PR #${item.prNumber}`,
        {
          description:
            item.status === "merged"
              ? "Approved, commented, and squash-merged."
              : item.blockers[0]
                ? asSentence(item.blockers[0])
                : "Merge is waiting.",
        },
      );
      refresh(taskId);
    },
    onError: fail("Could not approve and merge"),
  });
}

export function useResumeTask(taskId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (action: "rework" | "continue") => api.resumeTask(taskId, action),
    onSuccess: (_result, action) => {
      toast.success(action === "rework" ? "Sent to the Fixer" : "Continuing to business testing");
      refresh(taskId);
    },
    onError: fail("Could not resume the work order"),
  });
}

export function useCancelTask(taskId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: () => api.cancelTask(taskId),
    onSuccess: () => {
      toast.success("Work order pulled from the line");
      refresh(taskId);
    },
    onError: fail("Could not cancel the work order"),
  });
}

export function useCancelRun(taskId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (runId: string) => api.cancelRun(runId),
    onSuccess: () => {
      toast.success("Run cancelled");
      refresh(taskId);
    },
    onError: fail("Could not cancel the run"),
  });
}

export function useRetryTask(taskId: string) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { agentId?: string; provider?: string }) => api.retryTask(taskId, input),
    onSuccess: (_, input) => {
      toast.success(input.provider ? `Retrying on ${input.provider}` : "Retrying");
      refresh(taskId);
    },
    onError: fail("Could not retry"),
  });
}

export function useFollowUp(taskId: string) {
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, message }: { runId: string; message: string }) => api.followUp(runId, message),
    onSuccess: (_, { runId }) => {
      toast.success("Follow-up sent");
      queryClient.invalidateQueries({ queryKey: ["run-events", runId] });
      refresh(taskId);
    },
    onError: fail("Could not send the follow-up"),
  });
}
